(function () {
  const instances = new WeakMap();

  const getSectionFromCurrentScript = (sectionClass) => {
    const parent = document.currentScript?.parentElement;

    if (!parent) return null;

    if (sectionClass && !parent.classList.contains(sectionClass)) {
      return parent.closest(`.${sectionClass}`);
    }

    return parent;
  };

  const waitForImages = (root) => {
    const images = root.querySelectorAll("img");

    if (!images.length) return Promise.resolve();

    return Promise.all(
      [...images].map(
        (img) =>
          new Promise((resolve) => {
            if (img.complete) {
              resolve();
              return;
            }

            img.addEventListener("load", resolve, { once: true });
            img.addEventListener("error", resolve, { once: true });
          })
      )
    );
  };

  const removeDynamicClones = (wrapper) => {
    wrapper
      .querySelectorAll('[data-marquee-clone="true"]')
      .forEach((slide) => slide.remove());
  };

  const normalizeOffset = (offset, cycleWidth) => {
    if (!cycleWidth) return 0;

    return ((offset % cycleWidth) + cycleWidth) % cycleWidth;
  };

  const getCycleWidth = (wrapper, setSize, gap) => {
    const slides = [...wrapper.children];
    const firstSlide = slides[0];

    if (!firstSlide || setSize < 1) return 0;

    const nextSetSlide = slides[setSize];

    if (nextSetSlide) {
      return Math.max(nextSetSlide.offsetLeft - firstSlide.offsetLeft, 0);
    }

    const lastSlide = slides[Math.min(setSize, slides.length) - 1];

    if (!lastSlide) return 0;

    return Math.max(
      lastSlide.offsetLeft -
        firstSlide.offsetLeft +
        lastSlide.getBoundingClientRect().width +
        gap,
      0
    );
  };

  const destroyMarquee = (slider) => {
    const instance = instances.get(slider);
    const wrapper = slider.querySelector(".swiper-wrapper");

    if (!instance) return;

    instance.disconnect?.();
    instance.listeners?.forEach(({ target, type, handler, options }) => {
      target.removeEventListener(type, handler, options);
    });

    if (slider.swiper) {
      slider.swiper.destroy(true, true);
    }

    if (wrapper) {
      removeDynamicClones(wrapper);
      wrapper.style.removeProperty("transform");
    }

    slider.classList.remove(
      "continuous-marquee--css",
      "continuous-marquee--dragging",
      "continuous-marquee--paused"
    );
    slider.style.removeProperty("--marquee-gap");
    slider.style.removeProperty("--marquee-slide-width");
    delete slider.dataset.marqueeInitialized;
    instances.delete(slider);
  };

  const bindListener = (instance, target, type, handler, options) => {
    target.addEventListener(type, handler, options);
    instance.listeners.push({ target, type, handler, options });
  };

  const applySocialMediaSlideWidths = (slider, gap) => {
    if (!slider.classList.contains("social-media-slider")) return;

    const slidesPerView = parseFloat(slider.dataset.slidesPerView) || 1;
    const sliderWidth = slider.getBoundingClientRect().width;

    if (sliderWidth <= 0) return;

    const slideWidth =
      (sliderWidth - (slidesPerView - 1) * gap) / slidesPerView;

    slider.style.setProperty("--marquee-slide-width", `${slideWidth}px`);
  };

  const initContinuousMarquee = (slider, options) => {
    if (!slider) return null;

    destroyMarquee(slider);

    const wrapper = slider.querySelector(".swiper-wrapper");

    if (!wrapper || !wrapper.children.length) return null;

    removeDynamicClones(wrapper);

    const {
      speed = 4000,
      gap = 0,
      stopOnHover = false,
      marqueeCopies = 2,
    } = options;

    let copies = Math.max(1, marqueeCopies);

    if (copies === 1) {
      const slides = [...wrapper.children];

      slides.forEach((slide) => {
        const clone = slide.cloneNode(true);
        clone.dataset.marqueeClone = "true";
        wrapper.appendChild(clone);
      });

      copies = 2;
    }

    if (slider.swiper) {
      slider.swiper.destroy(true, true);
    }

    slider.classList.add("continuous-marquee--css");
    slider.style.setProperty("--marquee-gap", `${gap}px`);
    applySocialMediaSlideWidths(slider, gap);
    slider.dataset.marqueeInitialized = "true";

    const motionQuery = window.matchMedia?.("(prefers-reduced-motion: reduce)");

    const instance = {
      listeners: [],
      pauseReasons: new Set(),
      resumeTimers: {},
      cycleWidth: 0,
      offset: 0,
      velocity: 0,
      lastFrame: 0,
      suppressClicksUntil: 0,
      drag: {
        active: false,
        locked: false,
        horizontal: false,
        moved: false,
        startX: 0,
        startY: 0,
        startOffset: 0,
      },
      isPaused() {
        return this.pauseReasons.size > 0 || motionQuery?.matches;
      },
      syncPausedState() {
        slider.classList.toggle("continuous-marquee--paused", this.isPaused());
      },
      pause(reason) {
        this.pauseReasons.add(reason);
        this.syncPausedState();
      },
      resume(reason) {
        this.pauseReasons.delete(reason);
        this.syncPausedState();
      },
      scheduleResume(reason, delay) {
        clearTimeout(this.resumeTimers[reason]);
        this.resumeTimers[reason] = window.setTimeout(() => {
          delete this.resumeTimers[reason];
          this.resume(reason);
        }, delay);
      },
      setOffset(nextOffset) {
        this.offset = normalizeOffset(nextOffset, this.cycleWidth);
        wrapper.style.transform = `translate3d(${-this.offset}px, 0, 0)`;
      },
      disconnect() {
        this.intersectionObserver?.disconnect();
        this.resizeObserver?.disconnect();
        cancelAnimationFrame(this.rafId);
        Object.values(this.resumeTimers).forEach((timer) => {
          clearTimeout(timer);
        });
      },
    };

    const refreshLayout = () => {
      applySocialMediaSlideWidths(slider, gap);
      const setSize = Math.max(Math.floor(wrapper.children.length / copies), 1);
      const duration = Math.max(speed, 100) * setSize;

      instance.cycleWidth = getCycleWidth(wrapper, setSize, gap);
      instance.velocity =
        instance.cycleWidth > 0 && duration > 0 ? instance.cycleWidth / duration : 0;
      instance.setOffset(instance.offset);
    };

    instances.set(slider, instance);

    const getPoint = (event) => {
      if (event.touches?.length) return event.touches[0];
      if (event.changedTouches?.length) return event.changedTouches[0];

      return event;
    };

    const endDrag = () => {
      if (!instance.drag.active) return;

      slider.classList.remove("continuous-marquee--dragging");

      const shouldSuppressClicks = instance.drag.moved;

      instance.drag.active = false;
      instance.drag.locked = false;
      instance.drag.horizontal = false;
      instance.drag.moved = false;
      instance.resume("drag");

      if (shouldSuppressClicks) {
        instance.suppressClicksUntil = performance.now() + 250;
      }
    };

    const startDrag = (event) => {
      if (event.type === "mousedown" && event.button !== 0) return;

      const point = getPoint(event);

      if (!point) return;

      instance.drag.active = true;
      instance.drag.locked = false;
      instance.drag.horizontal = false;
      instance.drag.moved = false;
      instance.drag.startX = point.clientX;
      instance.drag.startY = point.clientY;
      instance.drag.startOffset = instance.offset;
      instance.pause("drag");
      slider.classList.add("continuous-marquee--dragging");

      if (event.type === "mousedown") {
        event.preventDefault();
      }
    };

    const moveDrag = (event) => {
      if (!instance.drag.active) return;

      const point = getPoint(event);

      if (!point) return;

      const deltaX = point.clientX - instance.drag.startX;
      const deltaY = point.clientY - instance.drag.startY;

      if (!instance.drag.locked) {
        if (Math.abs(deltaX) < 4 && Math.abs(deltaY) < 4) return;

        instance.drag.locked = true;
        instance.drag.horizontal = Math.abs(deltaX) >= Math.abs(deltaY);

        if (!instance.drag.horizontal) {
          endDrag();
          return;
        }
      }

      if (!instance.drag.horizontal) return;

      if (event.cancelable) {
        event.preventDefault();
      }

      instance.drag.moved = instance.drag.moved || Math.abs(deltaX) > 6;
      instance.setOffset(instance.drag.startOffset - deltaX);
    };

    const tick = (timestamp) => {
      if (!instance.lastFrame) {
        instance.lastFrame = timestamp;
      }

      const elapsed = timestamp - instance.lastFrame;
      instance.lastFrame = timestamp;

      if (!instance.isPaused() && instance.velocity > 0) {
        instance.setOffset(instance.offset + elapsed * instance.velocity);
      }

      instance.rafId = requestAnimationFrame(tick);
    };

    const intersectionObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.target !== slider) return;

          if (entry.isIntersecting) {
            instance.resume("offscreen");
          } else {
            instance.pause("offscreen");
          }
        });
      },
      { threshold: 0.05, rootMargin: "50px 0px" }
    );

    intersectionObserver.observe(slider);
    instance.intersectionObserver = intersectionObserver;

    if (typeof ResizeObserver !== "undefined") {
      let resizeTimer;

      const resizeObserver = new ResizeObserver(() => {
        clearTimeout(resizeTimer);
        resizeTimer = setTimeout(refreshLayout, 100);
      });

      resizeObserver.observe(slider);
      instance.resizeObserver = resizeObserver;
    }

    if (stopOnHover) {
      bindListener(instance, slider, "mouseenter", () => instance.pause("hover"));
      bindListener(instance, slider, "mouseleave", () => instance.resume("hover"));
    }

    const onVisible = () => {
      if (document.visibilityState === "visible") {
        instance.resume("document-hidden");
      } else {
        instance.pause("document-hidden");
      }
    };

    const onWheel = (event) => {
      const delta =
        Math.abs(event.deltaX) >= Math.abs(event.deltaY)
          ? event.deltaX
          : event.shiftKey
            ? event.deltaY
            : 0;

      if (!delta) return;

      if (event.cancelable) {
        event.preventDefault();
      }

      instance.pause("wheel");
      instance.setOffset(instance.offset + delta);
      instance.scheduleResume("wheel", 140);
    };

    bindListener(instance, document, "visibilitychange", onVisible);
    bindListener(instance, window, "pageshow", onVisible);
    bindListener(instance, slider, "mousedown", startDrag);
    bindListener(instance, slider, "touchstart", startDrag, { passive: true });
    bindListener(instance, window, "mousemove", moveDrag);
    bindListener(instance, window, "touchmove", moveDrag, { passive: false });
    bindListener(instance, window, "mouseup", endDrag);
    bindListener(instance, window, "mouseleave", endDrag);
    bindListener(instance, window, "touchend", endDrag);
    bindListener(instance, window, "touchcancel", endDrag);
    bindListener(instance, window, "blur", endDrag);
    bindListener(instance, slider, "dragstart", (event) => event.preventDefault());
    bindListener(
      instance,
      slider,
      "click",
      (event) => {
        if (performance.now() >= instance.suppressClicksUntil) return;

        event.preventDefault();
        event.stopPropagation();
      },
      true
    );
    bindListener(instance, slider, "wheel", onWheel, { passive: false });

    onVisible();
    instance.rafId = requestAnimationFrame(tick);

    waitForImages(wrapper).then(() => {
      requestAnimationFrame(() => {
        requestAnimationFrame(refreshLayout);
      });
    });

    return { cssMarquee: true, wrapper, refresh: refreshLayout };
  };

  window.theme = window.theme || {};
  window.theme.initContinuousMarquee = initContinuousMarquee;
  window.theme.destroyContinuousMarquee = destroyMarquee;
  window.theme.getSectionFromCurrentScript = getSectionFromCurrentScript;

  window.theme.initContinuousMarqueeSwiper = (slider, swiperConfig, options = {}) =>
    initContinuousMarquee(slider, {
      speed: swiperConfig?.speed ?? 4000,
      gap: options.gap ?? 0,
      stopOnHover: options.stopOnHover ?? false,
      marqueeCopies: options.marqueeCopies ?? 2,
    });

  window.theme.bindContinuousMarqueeHoverPause = () => {};
})();
